import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { USER_PREFERENCES_ACTIONS, USER_PREFERENCES_API_ACTIONS } from '../actions/user-preferences.actions';
import { catchError, first, map, skipWhile, switchMap, withLatestFrom } from 'rxjs/operators';
import { IUserPreferences } from '../../../shared/models/user-preferences.model';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { USER_PREFERENCES_REDUCER } from '../reducers/user-preferences.reducer';
import { AUTH_SELECTORS } from '../selectors/auth.selectors';
import { AUTH_ACTIONS } from '../actions/auth.actions';

@Injectable()
export class UserPreferencesEffects {
  constructor(
    private readonly actions: Actions,
    private readonly service: UserPreferencesService,
    private readonly store: Store,
  ) {}

  getUserPreferencesOnUserLoads$ = createEffect(() =>
    this.actions.pipe(
      ofType(AUTH_ACTIONS.userProfileLoaded),
      first(),
      map(() => USER_PREFERENCES_API_ACTIONS.get()),
    ),
  );

  uploadUpdatedUserPreferences$ = createEffect(() =>
    this.actions.pipe(
      ofType(USER_PREFERENCES_ACTIONS.update),
      switchMap(() => this.store.select(USER_PREFERENCES_REDUCER.selectGetError)),
      skipWhile((hasRetrieveError) => hasRetrieveError),
      map(() => USER_PREFERENCES_API_ACTIONS.upload()),
    ),
  );

  getUserPreferences$ = createEffect(() =>
    this.actions.pipe(
      ofType(USER_PREFERENCES_API_ACTIONS.get),
      withLatestFrom(this.store.select(AUTH_SELECTORS.userId)),
      switchMap(([, userId]) =>
        this.service.getPreferences(userId).pipe(
          map((data: IUserPreferences) => USER_PREFERENCES_API_ACTIONS.getSuccess({ data })),
          catchError(() => of(USER_PREFERENCES_API_ACTIONS.getError)),
        ),
      ),
    ),
  );

  uploadUserPreferences$ = createEffect(() =>
    this.actions.pipe(
      ofType(USER_PREFERENCES_API_ACTIONS.upload),
      withLatestFrom(
        this.store.select(USER_PREFERENCES_REDUCER['selectUser-preferencesState']),
        this.store.select(AUTH_SELECTORS.userId),
      ),
      skipWhile(([, { getError: hasGetError }]) => hasGetError),
      switchMap(([, { preferences }, userId]) =>
        this.service.savePreferences(preferences, userId).pipe(
          map(() => USER_PREFERENCES_API_ACTIONS.uploadSuccess()),
          catchError(() => of(USER_PREFERENCES_API_ACTIONS.uploadError)),
        ),
      ),
    ),
  );

  onPinNavigationChanged$ = createEffect(() =>
    this.actions.pipe(
      ofType(USER_PREFERENCES_ACTIONS.pinNavigation, USER_PREFERENCES_ACTIONS.unpinNavigation),
      map(({ type }) => {
        const isNavigationPinned = type === USER_PREFERENCES_ACTIONS.pinNavigation.type;
        return USER_PREFERENCES_ACTIONS.update({ data: { isNavigationPinned } });
      }),
    ),
  );

  toggleFavouritePage$ = createEffect(() =>
    this.actions.pipe(
      ofType(USER_PREFERENCES_ACTIONS.toggleFavouritePage),
      withLatestFrom(this.store.select(USER_PREFERENCES_REDUCER.favouritePagesUrl)),
      map(([{ pageUrl }, favouritePages]) => {
        const isAlreadyFavourite = favouritePages.includes(pageUrl);
        const updatedFavouritePages = isAlreadyFavourite
          ? favouritePages.filter((url) => url !== pageUrl)
          : [...favouritePages, pageUrl];

        return USER_PREFERENCES_ACTIONS.update({ data: { favouritePagesUrl: updatedFavouritePages } });
      }),
    ),
  );
}
